import React, { useState, useContext } from "react";
import {
  required,
  Button,
  SaveButton,
  TextInput,
  useCreate,
  useNotify,
  FormWithRedirect,
  SelectInput,
  useRedirect
} from "react-admin";
import Alert from "@material-ui/lab/Alert";
import IconContentAdd from "@material-ui/icons/Add";
import IconCancel from "@material-ui/icons/Cancel";
import { compareTwoStrings } from "string-similarity";

import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import { PlayByPlayContext } from "games/model/PlayByPlayContext";
import _ from "lodash";

function PlayerQuickCreateButton(props) {
  const {
    usePlayByPlay: [state],
    setShouldUpdateData,
    styles
  } = useContext(PlayByPlayContext);
  const { game } = state;
  const [showDialog, setShowDialog] = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  const [warning, setWarning] = useState("");
  const [teamRef, setTeamRef] = useState("");
  const notify = useNotify();
  const homeSeason = _.get(game, "home.season");
  const awaySeason = _.get(game, "away.season");
  const redirectTo = useRedirect();

  const useCreatePeople = useCreate("people");
  const createPerson = _.get(useCreatePeople, "0");
  const createPersonLoading = _.get(useCreatePeople, "[1].loading");

  const useCreatePlayer = useCreate("players");
  const createPlayer = _.get(useCreatePlayer, "0");
  const createPlayerLoading = _.get(useCreatePlayer, "[1].loading");

  const isLoading = createPersonLoading || createPlayerLoading;

  const handleClick = () => {
    setShowDialog(true);
  };

  const handleCloseClick = () => {
    setShowDialog(false);
    setShowVerify(false);
    setWarning("");
    setTeamRef("");
  };

  const handleSubmit = async (values, redirect) => {
    const { firstName, lastName, middleInitial, seasonTeam } = values;
    const personData = {
      firstName,
      lastName,
      middleInitial,
      isStub: true
    };

    const side = homeSeason.id === seasonTeam ? homeSeason : awaySeason;
    const fullName = `${firstName} ${lastName}`;
    const playersRaw = _.get(side, "players");
    const players = playersRaw.map(
      (val) => `${val?.person?.firstName} ${val?.person?.lastName}`
    );
    const filtered = players.filter((val) => {
      // Comparing against .6 based on personal preference and various testing of misspellings / intentionally trying to trigger duplicates
      return compareTwoStrings(val.toLowerCase(), fullName.toLowerCase()) > 0.6;
    });

    let handleCreate = () => {
      setShowVerify(false);
      setWarning("");
      setTeamRef("");
      createPerson(
        { payload: { data: personData } },
        {
          onSuccess: ({ data }) => {
            const {
              position,
              additionalPositions,
              jerseyNumber,
              yearCode,
              seasonTeam
            } = values;
            const playerData = {
              season_team_id: seasonTeam,
              sportCode: "football",
              person: {
                id: data.id
              },
              isActive: true,
              position,
              additionalPositions,
              jerseyNumber,
              yearCode
            };
            createPlayer(
              { payload: { data: playerData } },
              {
                onSuccess: () => {
                  setShouldUpdateData(true);
                  setShowDialog(false);
                  redirectTo(redirect, props.basePath, game.id);
                },
                onFailure: ({ error }) => {
                  notify(error.message, "error");
                }
              }
            );
          },
          onFailure: ({ error }) => {
            notify(error.message, "error");
          }
        }
      );
    };

    if (showVerify && teamRef === seasonTeam) {
      handleCreate();
    } else if (filtered.length > 0) {
      setShowVerify(true);
      notify(`Similar player found on SeasonTeam: ${filtered.toString()}`);
      setWarning(
        `Similarly named player found on SeasonTeam: ${filtered.toString()}`
      );
      setTeamRef(seasonTeam);
    } else {
      handleCreate();
    }
  };

  return (
    <>
      <Button onClick={handleClick} label="Add Player" variant="outlined">
        <IconContentAdd />
      </Button>
      <Dialog
        fullWidth
        open={showDialog}
        onClose={handleCloseClick}
        aria-label="Create Player"
      >
        <DialogTitle>Create Player</DialogTitle>

        <FormWithRedirect
          save={handleSubmit}
          render={({ handleSubmitWithRedirect, pristine, saving }) => (
            <>
              <DialogContent>
                <TextInput source="firstName" validate={required()} fullWidth />
                <TextInput source="lastName" validate={required()} fullWidth />
                <TextInput source="middleInitial" fullWidth />
                <TextInput source="jerseyNumber" className={styles.input} />
                <TextInput source="position" className={styles.input} />
                <TextInput
                  source="additionalPositions"
                  className={styles.input}
                />
                <SelectInput
                  source="yearCode"
                  className={styles.input}
                  choices={[
                    { id: "FRESHMAN", name: "FRESHMAN" },
                    { id: "SOPHOMORE", name: "SOPHOMORE" },
                    { id: "JUNIOR", name: "JUNIOR" },
                    { id: "SENIOR", name: "SENIOR" }
                  ]}
                />
                <SelectInput
                  validate={required()}
                  className={styles.input}
                  source="seasonTeam"
                  label="Team"
                  choices={[
                    {
                      id: _.get(homeSeason, "id"),
                      name: _.get(homeSeason, "team.school.name")
                    },
                    {
                      id: _.get(awaySeason, "id"),
                      name: _.get(awaySeason, "team.school.name")
                    }
                  ]}
                />

                {warning.length > 0 ? (
                  <Alert severity="warning">{warning}</Alert>
                ) : null}
              </DialogContent>
              <DialogActions>
                <Button
                  label="ra.action.cancel"
                  onClick={handleCloseClick}
                  disabled={isLoading}
                >
                  <IconCancel />
                </Button>
                {showVerify ? (
                  <SaveButton
                    label={"Proceed with Save"}
                    handleSubmitWithRedirect={handleSubmitWithRedirect}
                    pristine={pristine}
                    saving={saving}
                    disabled={isLoading}
                  />
                ) : (
                  <SaveButton
                    handleSubmitWithRedirect={handleSubmitWithRedirect}
                    pristine={pristine}
                    saving={saving}
                    disabled={isLoading}
                  />
                )}
              </DialogActions>
            </>
          )}
        />
      </Dialog>
    </>
  );
}

export default PlayerQuickCreateButton;
